<script setup>
const store = useCommonStore();

import { pageTransition } from '~/helpers/animations/animations';
definePageMeta({
  pageTransition: pageTransition,
});

const eventFiltered = ref(null);

const localePath = useLocalePath();
const route = useRoute();
const gtm = useGtm();
const { t } = useI18n();
const setI18nParams = useSetI18nParams();
const pageColor = computed(() => store.pageColor);

const beforeRouteEnter = (to, from, next) => {
  next((vm) => {
    vm.previous = from.fullPath;
  });
};

eventFiltered.value = () => {
  if (page.value?.reservation_type !== 'NONE' && page.value?.reservation_type !== 'GOOUT') {
    gtm.trackEvent({
      event: 'eventDetail_reservationOpen',
      eventId: route.params.id,
      url: previous.value,
    });
  }

  if (page.value?.reservation_type === 'GOOUT') {
    gtm.trackEvent({
      event: 'eventDetail_ticket',
      eventId: route.params.id,
      url: previous.value,
    });
  }
};

const eventQuery = gql`
  query ($id: ID!) {
    event(id: $id) {
      id
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      type
      page_background_color
      slug: seo_url_slug {
        cs
        en
      }
      hide_building_opening
      categories {
        id
      }
      buildings {
        id
        name
        slug: seo_url_slug {
          cs
          en
        }
      }
      display_duration
      dates {
        id
        name
        building {
          id
          name
          slug: seo_url_slug {
            cs
            en
          }
        }
        start_at {
          timestamp
          localized
        }
        end_at {
          timestamp
          localized
        }
      }
      registration_type
      reservation_type
      admission {
        is_hidden
        is_free
        prices {
          type {
            id
            name
          }
          price
          description
        }
      }
    }
  }
`;

const { data } = await useAsyncQuery(eventQuery, { id: route.params.id });
const page = ref(data.value?.event);
const chosenDate = ref(page.value?.dates.find((date) => date.id == route.params.ticketId));

function getBuilding(obj) {
  if (obj && obj.buildings) {
    return obj.buildings;
  } else if (page.value && page.value?.buildings) {
    return page.value?.buildings;
  } else {
    return null;
  }
}

const bgColor = ref(page.value?.page_background_color);
store.setPageColor(bgColor.value);
onMounted(() => {
  store.setPageColor(bgColor.value);
});
onBeforeRouteLeave(() => {
  store.setPageColor(null);
});

setI18nParams({
  en: {
    slug: page?.value?.slug?.en,
  },
  cs: {
    slug: page?.value?.slug?.cs,
  },
});

const breadcrumbs = [
  {
    title: t('pages.events.breadcrumbs-title'),
    route: 'events',
  },
  {
    title: t('pages.event-detail.breadcrumbs-title'),
    route: { name: 'event-id-slug', params: { id: route.params.id, slug: route.params.slug } },
  },
  {
    title: t('pages.event-tickets.breadcrumbs-title'),
  },
];

const getCategoryId = (item) => {
  if (item.categories && item.categories[0] && item.categories[0].id) {
    return item.categories[0].id;
  } else {
    return null;
  }
};

useHeadData({
  title: page.value
    ? page.value?.seo_meta_title
      ? t('pages.event-detail.tickets') + ' - ' + page.value?.seo_meta_title
      : t('pages.event-detail.tickets') + ' - ' + page.value?.name
    : t('pages.event-detail.tickets'),
  description: page.value && page.value?.seo_meta_description ? page.value?.seo_meta_description : null,
  keywords: page.value && page.value?.seo_meta_keywords ? page.value?.seo_meta_keywords : null,
  image: null,
  url: route.fullPath,
});
</script>

<template>
  <main :style="{ backgroundColor: bgColor, '--background-color': bgColor }" class="main">
    <div class="event-detail">
      <br />

      <Breadcrumbs :items="breadcrumbs" />
      <div class="container-max-content">
        <div class="text text--2 fwm upper">
          {{ t(`global.event-types.${page.type.toUpperCase()}`) }}
        </div>
        <h1 class="heading mt-2">{{ page.name }}</h1>
        <div class="event-detail__meta">
          <EventDateStrip
            :reservation-type="page.reservation_type"
            :date="chosenDate"
            :time="chosenDate.start_at.timestamp"
            :time-stamp-end="chosenDate?.end_at?.timestamp"
            :locations="!page.online ? getBuilding(page.buildings) : null"
            :type="page.type"
            :category-id="getCategoryId(page)"
            noButton="true"
            noExtras="true"
            :no-link="true"
            :bold="true"
            :duration-display="event?.display_duration || false"
            :hide-building-opening="event?.hide_building_opening || null"
          />
        </div>
        <div v-if="page.admission && !page.admission.is_hidden" class="mt-5">
          <h1 v-if="!page.admission.is_free" class="heading heading--3">
            {{ t('pages.event.tickets-title') }}
          </h1>
          <h1 v-else class="heading heading--3">
            {{ t('pages.event.tickets-free') }}
          </h1>
          <p class="text text--1 mt-3" v-for="(item, key) in page.admission.prices">
            <span :key="key">
              <span>{{ item.type.name }} <template v-if="item.price != '0'">:</template> </span><strong v-if="item.price != '0'"> {{ item.price }} Kč</strong
              ><span>{{ item.description ? ' – ' + item.description : '' }}</span>
            </span>
          </p>
          <p class="text text--3 mt-2">
            <span>
              {{ t('pages.club.buy.pricelist') }}
              <nuxt-link :to="localePath({ name: 'about-admission' })" class="link">{{ t('pages.club.buy.pricelist.link') }}</nuxt-link
              >.
              {{ t('pages.club.buy.school-pricelist') }}
              <nuxt-link :to="localePath({ name: 'about-admission' })" class="link">{{ t('pages.club.buy.school-pricelist.link') }}</nuxt-link
              >.
            </span>
          </p>
        </div>
        <ReservationForm :event="page" />
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.event-detail {
  &__header {
    padding-bottom: 40px;
    @include mq($sm) {
      display: flex;
      flex-flow: column;
      margin-top: #{$mainPaddingTop * -1};
      min-height: 100vh;
      padding-top: $mainPaddingTop;
    }

    @include mq($lg) {
      margin-top: #{$mainLgPaddingTop * -1};
      padding-top: $mainLgPaddingTop;
    }
  }

  &__heading {
    flex-grow: 0;
  }

  &__meta {
    border-bottom: 1px solid $black;
    border-top: 1px solid $black;
    margin-bottom: 40px;
    margin-top: 40px;
    // padding-bottom: 15px;

    &:first-child {
      margin-top: 0;
    }

    .event-detail__extra-date {
      &:last-child {
        border-bottom: 0;
      }
    }
  }

  &__hero {
    overflow: hidden;
    @include mq($sm) {
      flex-grow: 1;
      min-height: 50vh;
      // overflow-x: auto;
      position: relative;
    }

    @include mq($md) {
      min-height: 0;
    }
  }

  &__hero-image {
    display: block;
    width: 100%;
    overflow: hidden;

    @include mq($sm) {
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      object-position: left top;
      object-fit: contain;
      width: auto;
    }
    @include mq($lg) {
      object-position: center;
      object-fit: cover;
    }
  }

  &__tags {
  }

  &__tags-title {
    font-weight: 500;
    text-transform: uppercase;
  }

  &__tags-list {
    display: inline-block;
  }

  &__tag {
    display: inline-block;

    &:after {
      content: ', ';
    }

    &:last-child {
      &:after {
        display: none;
      }
    }
  }

  &__people {
  }

  &__person {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__extra-dates {
    //max-width: $content-max-width;
  }

  &__extra-date {
    //padding: 20px 0;
    border-bottom: 1px solid rgba($black, 0.3);

    &:last-child {
      border-bottom: 1px solid $black;
    }
  }

  &__fb {
    a {
      .facebook-text {
        text-decoration: underline;
      }

      &:hover {
        .facebook-text {
          text-decoration: none;
        }
      }
    }
    span {
      display: inline-block;
      vertical-align: middle;
    }
    .icon-facebook {
      margin-right: 12px;
      font-size: 32px;
    }
  }
}

.detail-meta {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid $black;
  border-bottom: 1px solid $black;

  &__item {
    display: flex;
    margin-right: 30px;

    &--end {
      margin-left: auto;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  &__icon {
    margin-right: 8px;
  }
}
</style>
